/**
 * Contractor Portal Layout - MaintainX Style
 * Enterprise-grade UI with sidebar navigation for contractor dashboard
 */

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Wrench,
  Briefcase,
  FileText,
  DollarSign,
  Users,
  Settings,
  Shield,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Menu,
  X,
  HardHat,
  Clock,
  CheckCircle,
  Package,
  TrendingUp,
  Award,
  Megaphone,
  Home as HomeIcon,
  Inbox,
} from "lucide-react";

// Navigation items for contractor portal
const navItems = [
  {
    id: "home",
    label: "Home",
    icon: HomeIcon,
    path: "/",
    exact: true,
    description: "Public homepage",
  },
  {
    id: "dashboard",
    label: "Dashboard Overview",
    icon: LayoutDashboard,
    path: "/contractor/dashboard?tab=overview",
    tab: "overview",
  },
  {
    id: "rfqs",
    label: "My RFQs",
    icon: Inbox,
    path: "/contractor/dashboard?tab=rfqs",
    tab: "rfqs",
    description: "Requests from admin",
  },
  { 
    id: "jobs", 
    label: "My Jobs", 
    icon: Briefcase, 
    path: "/contractor/dashboard?tab=jobs" 
  },
  { 
    id: "work-orders", 
    label: "Active Work Orders", 
    icon: HardHat,
    path: "/contractor/dashboard?tab=work-orders",
    tab: "work-orders",
    description: "Live updates, checklists, photos",
  },
  { 
    id: "job-board", 
    label: "Job Board", 
    icon: Megaphone, 
    path: "/contractor/dashboard?tab=job-board" 
  },
  { type: "divider" },
  { 
    id: "earnings", 
    label: "Earnings", 
    icon: TrendingUp, 
    path: "/contractor/dashboard?tab=earnings" 
  },
  { 
    id: "invoices", 
    label: "Invoices", 
    icon: DollarSign, 
    path: "/contractor/dashboard?tab=invoices" 
  },
  { 
    id: "team", 
    label: "My Team", 
    icon: Users, 
    path: "/contractor/dashboard?tab=team" 
  },
  { type: "divider" },
  { 
    id: "certification", 
    label: "Certification", 
    icon: Award, 
    path: "/contractor/dashboard?tab=certification" 
  },
  { 
    id: "profile", 
    label: "Company Profile", 
    icon: HardHat, 
    path: "/contractor/profile" 
  },
  { 
    id: "credentials", 
    label: "Credentials", 
    icon: Shield, 
    path: "/contractor/profile?tab=credentials" 
  },
  { type: "divider" },
  { 
    id: "settings", 
    label: "Settings", 
    icon: Settings, 
    path: "/contractor/dashboard?tab=settings" 
  },
];

// Sidebar Component
const Sidebar = ({ collapsed, setCollapsed, contractor, onLogout }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "home";

  const isActive = (item) => {
    if (item.external) return false;
    if (item.tab) return currentTab === item.tab;
    if (item.exact) {
      return location.pathname === item.path.split('?')[0] && !location.search;
    }
    if (item.path.includes('?')) {
      const [path, query] = item.path.split('?');
      return location.pathname === path && location.search.includes(query);
    }
    return location.pathname === item.path;
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 transition-all duration-300 z-50 flex flex-col ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="contractor-sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <Link to="/contractor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Contractor</span>
          </Link>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center mx-auto">
            <HardHat className="w-5 h-5 text-white" />
          </div>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-400 hover:text-white p-1 rounded hidden lg:block"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {navItems.map((item, index) => {
            if (item.type === "divider") {
              return (
                <li key={`divider-${index}`} className="my-3">
                  <div className="h-px bg-slate-800 mx-2" />
                </li>
              );
            }

            const Icon = item.icon;
            const active = isActive(item);

            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group ${
                    active
                      ? "bg-emerald-600 text-white"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile & Logout */}
      <div className="border-t border-slate-800 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
              <User size={18} className="text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {contractor?.company_name || contractor?.business_name || "Contractor"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {contractor?.email || ""}
              </p>
            </div>
          </div>
        ) : null}
        <button
          onClick={onLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
          title="Logout"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

// Top Header Component
const TopHeader = ({ title, subtitle, actions, collapsed }) => {
  return (
    <header 
      className={`sticky top-0 z-40 bg-white border-b border-slate-200 h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${
        collapsed ? "lg:ml-16" : "lg:ml-64"
      }`}
      data-testid="top-header"
    >
      <div className="ml-10 lg:ml-0">
        <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{subtitle}</p>}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notifications */}
        <button 
          className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="notifications-btn"
        >
          <Bell size={20} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>
        
        {/* Custom Actions */}
        {actions}
      </div>
    </header>
  );
};

// Main Layout Export
export default function ContractorLayout({ 
  children, 
  title = "Dashboard", 
  subtitle,
  actions,
  contractor = null,
  onLogout = () => {},
  hideTopHeader = false,
  mainPadding = "p-4 sm:p-6"
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile unless menu open */}
      <div className={`lg:block ${mobileMenuOpen ? "block" : "hidden"}`}>
        <Sidebar 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          contractor={contractor}
          onLogout={onLogout}
        />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        {!hideTopHeader && (
          <TopHeader 
            title={title} 
            subtitle={subtitle}
            actions={actions}
            collapsed={collapsed}
          />
        )}
        
        <main className={mainPadding} data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export { Sidebar, TopHeader };
